/**
 * The setup checklist — FR-I12, T-4.14,
 * [dukaan §1](../../../../../docs/legacy/dukaan.md)'s last step: **progress**.
 * A checklist that knows what is done.
 *
 * **Every row is computed from the data, and none of it is stored.** A stored
 * "domain bound" flag is a flag that still says bound after the binding lapses,
 * and a checklist that lies about the one thing a firm cannot see for itself is
 * worse than no checklist. So each row is a read, and a read that fails leaves
 * its row unticked rather than ticking it optimistically — the same fail-closed
 * shape everything else in this app uses for a question it could not get an
 * answer to.
 *
 * **Bar the firm's own word** (spec C3). Whoever holds `settings:can_update`
 * may mark an unticked row done or skip it — a firm with no domain and no plan
 * for one should not carry "Bind your domain" forever. That is stored, because
 * no read could find it, and it is shown for what it is: a tag and an Undo,
 * never a tick indistinguishable from the data's. The data outranks it.
 *
 * **It hides itself when it is done** (FR-I12). A permanent nine-row panel on
 * the home screen of a firm in its third year is furniture; the checklist is
 * for the first week and it should know when the first week is over.
 *
 * **This replaces the Overview seam.** `routes/Overview.tsx` carried an honest
 * paragraph saying the dashboard was the business team's — which was true when
 * it was written and is what a firm sees on its very first sign-in. Nine rows a
 * firm can act on is a better first screen than a sentence about who owns the
 * roadmap.
 */

import { Alert, Badge, Button, Card, CardBody } from '@fiveox/ui'
import { ArrowRight, Check, Circle, CircleSlash } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'

import { CASE, CLIENT, SERVICE, SETTINGS, SITE, useEvaluation } from '../../lib/access'
import { cases } from '../../lib/api/cases'
import { clients } from '../../lib/api/clients'
import { firm, type IntegrationCard } from '../../lib/api/firm'
import { services, type Service } from '../../lib/api/services'
import {
  checklist,
  checklistProgress,
  site,
  withMarks,
  type ChecklistMark,
  type ChecklistMarkState,
  type ChecklistRow,
  type Page,
} from '../../lib/api/site'

/** The channels a firm needs before ProDesk can send anything on its behalf.
 *  `active` is the only state that counts — `unavailable` means *we could not
 *  ask*, which is not the same as *configured* and must never tick a row. */
const CHANNEL_CODES = ['smtp', 'email', 'whatsapp', 'sms']

export function SetupChecklist() {
  const { can, evaluation } = useEvaluation()
  const [rows, setRows] = useState<ChecklistRow[] | null>(null)
  const [marks, setMarks] = useState<ChecklistMark[]>([])
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  // A panel that vanishes under the pointer the moment its last row is skipped
  // takes the Undo with it. So the panel that was finished *on this visit*
  // stays, says so, and is gone on the next one.
  const [finishedHere, setFinishedHere] = useState(false)
  const canMark = can(SETTINGS, 'can_update')

  useEffect(() => {
    // **Not before the verb map has loaded.** `can` answers false for
    // everything until then, so running early would paint a real checklist
    // with nine unticked rows and then correct itself — a firm's first
    // impression of the product being that it has done nothing.
    if (!evaluation) return
    let cancelled = false

    // Each read is independently allowed to fail. A firm whose case service is
    // briefly unreachable should still see the other eight rows, not a blank
    // panel — and the failed row stays unticked, which is the truthful answer
    // to "we could not find out".
    const ask = async <T,>(allowed: boolean, work: () => Promise<T>, whenItFails: T): Promise<T> => {
      if (!allowed) return whenItFails
      try {
        return await work()
      } catch {
        return whenItFails
      }
    }

    void (async () => {
      const [profile, read, pages, catalog, domain, integrations, clientCount, caseCount, marked] =
        await Promise.all([
          ask(can(SETTINGS, 'can_view'), async () => (await firm.read()).data.profile, null),
          ask(can(SITE, 'can_view'), async () => (await site.read()).data, null),
          ask(can(SITE, 'can_view'), async () => (await site.pages('draft')).data, []),
          ask(
            can(SERVICE, 'can_view'),
            async () => (await services.list()).data.services,
            [],
          ),
          ask(can(SETTINGS, 'can_view'), async () => (await firm.domain()).data, null),
          ask(can(SETTINGS, 'can_view'), async () => (await firm.integrations()).data, null),
          ask(
            can(CLIENT, 'can_view'),
            async () => Number((await clients.list({ limit: 1 })).meta?.total ?? 0),
            0,
          ),
          ask(
            can(CASE, 'can_view'),
            async () => (await cases.queue('all', { limit: 1 })).data.length,
            0,
          ),
          // C3. A failed read shows every unticked row as open — more to do
          // than there is, never less.
          ask(can(SITE, 'can_view'), async () => (await firm.checklistMarks()).data.marks, []),
        ])

      if (cancelled) return

      // How many pages actually carry something. One read per page, and the
      // list is at most a dozen — the alternative is a count the service does
      // not offer and this screen does not warrant adding.
      const detailed = await Promise.all(
        pages.map((page: Page) =>
          site
            .page(page.id)
            .then((body) => (body.data.blocks?.length ?? 0) > 0)
            .catch(() => false),
        ),
      )
      if (cancelled) return

      setMarks(marked)
      setRows(
        checklist({
          profession: profile?.profession ?? null,
          // w7a — ACCEPTANCE §3.5. This was `display_name`, **which no screen
          // writes**: the wizard and Settings both collect `legal_name`, so the
          // first row of the checklist could never be ticked and a firm that
          // had finished all four wizard steps still opened on "1 of 9 done".
          firmName: profile?.legal_name ?? null,
          // w7a — and this was `read?.site.template`, which is `NOT NULL` with
          // a `server_default`, so the row ticked **before a firm picked
          // anything**: the one of "1 of 9" was a choice nobody made.
          // `website_template` is nullable and is the firm's own act; the two
          // columns are now kept in step by `domain/site.py: adopt_template`.
          template: profile?.website_template ?? null,
          pagesWithContent: detailed.filter(Boolean).length,
          serviceCount: catalog.length,
          pricedServiceCount: catalog.filter((service: Service) =>
            (service.plans ?? []).some((plan) => plan.priced),
          ).length,
          domainBound: domain?.state === 'bound',
          channelsConfigured: (integrations?.cards ?? []).some(
            (card: IntegrationCard) =>
              CHANNEL_CODES.includes(card.code) && card.state === 'active',
          ),
          gatewayConfigured: (integrations?.cards ?? []).some(
            (card: IntegrationCard) => card.category === 'payments' && card.state === 'active',
          ),
          clientCount,
          caseCount,
          published: Boolean(read?.is_live),
        }),
      )
    })()

    return () => {
      cancelled = true
    }
  }, [can, evaluation])

  if (!rows) return null

  const shown = withMarks(rows, marks)
  const { done, skipped, total, complete } = checklistProgress(shown)
  // FR-I12: hidden once complete — by data, by mark, or skipped (C3).
  if (complete && !finishedHere) return null

  const skipAll = async () => {
    setBusyKey('*')
    setFailed(false)
    try {
      setMarks((await firm.skipChecklist('skipped')).data.marks)
      setFinishedHere(true)
    } catch {
      setFailed(true)
    } finally {
      setBusyKey(null)
    }
  }

  const mark = async (rowKey: string, state: ChecklistMarkState | null) => {
    setBusyKey(rowKey)
    setFailed(false)
    try {
      const next = (await firm.markChecklistRow(rowKey, state)).data.marks
      setMarks(next)
      if (checklistProgress(withMarks(rows, next)).complete) setFinishedHere(true)
    } catch {
      setFailed(true)
    } finally {
      setBusyKey(null)
    }
  }

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1">
            <h2 className="text-base font-semibold text-foreground">Getting set up</h2>
            <p className="text-xs text-muted-foreground">
              {complete
                ? 'All set. This list will not show again — Settings brings it back.'
                : `${done} of ${total} done${skipped ? ` · ${skipped} skipped` : ''}. Nothing here is urgent — each one unlocks a bit more of the product.`}
            </p>
          </div>
          {/* C3. Skipping the list is not losing it: Settings shows it again,
              and the sentence says so before the firm presses anything. */}
          {canMark && !complete && (
            <Button variant="ghost" size="sm" disabled={busyKey === '*'} onClick={() => void skipAll()}>
              Skip setup
            </Button>
          )}
          <div
            className="h-1.5 w-32 overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-valuenow={done + skipped}
            aria-valuemin={0}
            aria-valuemax={total}
            aria-label="Setup progress"
          >
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${((done + skipped) / total) * 100}%` }}
            />
          </div>
        </div>

        {failed && <Alert tone="error">That did not save. Try again in a moment.</Alert>}

        <ul className="divide-y divide-border">
          {shown.map((row) => {
            const closed = row.done || Boolean(row.mark)
            const busy = busyKey === row.key
            return (
              <li key={row.key} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5">
                <Link
                  to={row.href}
                  className="flex min-w-0 flex-1 items-center gap-3 transition-colors hover:text-primary"
                >
                  {row.done || row.mark === 'done' ? (
                    <Check size={16} className="shrink-0 text-emerald-700" />
                  ) : row.mark === 'skipped' ? (
                    <CircleSlash size={16} className="shrink-0 text-muted-foreground" />
                  ) : (
                    <Circle size={16} className="shrink-0 text-muted-foreground" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block text-sm ${
                        closed ? 'text-muted-foreground line-through' : 'text-foreground'
                      }`}
                    >
                      {row.label}
                    </span>
                    {!closed && (
                      <span className="block text-xs text-muted-foreground">{row.hint}</span>
                    )}
                  </span>
                  {!closed && <ArrowRight size={14} className="shrink-0 text-muted-foreground" />}
                </Link>

                {/* w8h, finding 2. The second screen this row's work is done on,
                    outside the row's own `Link` because it is a different
                    destination and nesting one anchor in another is not a link
                    a keyboard can reach. Shown whatever the row's state: a firm
                    that has written its four pages is precisely the firm that
                    then wants its logo on them. */}
                {row.also && (
                  <Link
                    to={row.also.href}
                    className="shrink-0 text-xs text-muted-foreground underline transition-colors hover:text-primary"
                  >
                    {row.also.label}
                  </Link>
                )}

                {/* C3. The tag says whose word closed the row — the firm's, not
                    the data's — and Undo hands it back to the data. */}
                {row.mark && (
                  <span className="flex items-center gap-2">
                    <Badge tone="neutral">{row.mark === 'done' ? 'Marked done' : 'Skipped'}</Badge>
                    {canMark && (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => void mark(row.key, null)}
                      >
                        Undo
                      </Button>
                    )}
                  </span>
                )}
                {!closed && canMark && (
                  <span className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => void mark(row.key, 'done')}
                    >
                      Mark done
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => void mark(row.key, 'skipped')}
                    >
                      Skip
                    </Button>
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      </CardBody>
    </Card>
  )
}

/** Shown when a firm has no `site` permission at all — so the Overview does not
 *  render an empty space where a checklist would be. */
export function ChecklistUnavailable() {
  return (
    <Alert tone="info">
      Setting up the firm&rsquo;s website and services is a partner&rsquo;s or a practice
      manager&rsquo;s to do.
    </Alert>
  )
}
